import React from 'react';
import { Check, Pencil } from 'lucide-react';
import {
  MANDATS_DRING_LABELS,
  buildMandatsAkteTitle,
  type MandatsannahmeSlots,
} from './parseMandatsSlots';

const DRING_OPTIONS: Array<{ value: string; label: string }> = [
  { value: MANDATS_DRING_LABELS.niedrig, label: MANDATS_DRING_LABELS.niedrig },
  { value: MANDATS_DRING_LABELS.mittel, label: MANDATS_DRING_LABELS.mittel },
  { value: MANDATS_DRING_LABELS.hoch, label: MANDATS_DRING_LABELS.hoch },
];

const FIELD_INPUT =
  'w-full rounded-md border border-white/10 bg-slate-950/40 px-2.5 py-1.5 text-sm text-slate-100 outline-none placeholder:text-slate-600 focus:border-violet-400/40';

export interface PortalMandatsConfirmCardProps {
  slots: MandatsannahmeSlots;
  editing: boolean;
  draft: MandatsannahmeSlots;
  disabled?: boolean;
  onStartEdit: () => void;
  onDraftChange: (next: MandatsannahmeSlots) => void;
  onSave: () => void;
  onCancel: () => void;
}

function SlotRow({ label, value }: { label: string; value?: string }) {
  return (
    <div className="grid grid-cols-[7.5rem_minmax(0,1fr)] gap-2 items-start">
      <dt className="text-[11px] font-medium uppercase tracking-wide text-slate-500 pt-0.5">
        {label}
      </dt>
      <dd className="text-sm text-slate-100 break-words">{value?.trim() || '—'}</dd>
    </div>
  );
}

export const PortalMandatsConfirmCard: React.FC<PortalMandatsConfirmCardProps> = ({
  slots,
  editing,
  draft,
  disabled = false,
  onStartEdit,
  onDraftChange,
  onSave,
  onCancel,
}) => {
  const previewSlots = editing ? { ...slots, ...draft } : slots;
  const previewTitle = buildMandatsAkteTitle(previewSlots);

  return (
    <div className="w-full overflow-hidden border border-violet-500/25 bg-violet-500/[0.06] rounded-lg">
      <div className="flex items-center justify-between gap-2 border-b border-violet-500/20 bg-violet-500/[0.08] px-3 py-1.5">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-violet-100/90">
          Mandatsannahme
        </span>
        {editing ? (
          <span className="text-[10px] text-violet-200/70">Felder korrigieren, dann speichern</span>
        ) : (
          <span className="text-[10px] text-slate-500">Vor der Akte prüfen</span>
        )}
      </div>

      {editing ? (
        <div className="px-3 py-2.5 space-y-2.5">
          <label className="block">
            <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-slate-500">
              Mandant
            </span>
            <input
              type="text"
              value={draft.mandant ?? ''}
              onChange={(e) => onDraftChange({ ...draft, mandant: e.target.value })}
              className={FIELD_INPUT}
              aria-label="Mandant bearbeiten"
              maxLength={80}
              disabled={disabled}
              spellCheck
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-slate-500">
              Gegenstand
            </span>
            <textarea
              value={draft.gegenstand ?? ''}
              onChange={(e) => onDraftChange({ ...draft, gegenstand: e.target.value })}
              rows={3}
              className={`${FIELD_INPUT} resize-y min-h-[4.5rem]`}
              aria-label="Gegenstand bearbeiten"
              maxLength={120}
              disabled={disabled}
              spellCheck
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-slate-500">
              Dringlichkeit
            </span>
            <select
              value={draft.dringlichkeit ?? ''}
              onChange={(e) => onDraftChange({ ...draft, dringlichkeit: e.target.value })}
              className={FIELD_INPUT}
              aria-label="Dringlichkeit bearbeiten"
              disabled={disabled}
            >
              <option value="">— unverändert —</option>
              {DRING_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      ) : (
        <dl className="px-3 py-2.5 space-y-2">
          <SlotRow label="Mandant" value={slots.mandant} />
          <SlotRow label="Gegenstand" value={slots.gegenstand} />
          <SlotRow label="Dringlichkeit" value={slots.dringlichkeit} />
        </dl>
      )}

      <div className="border-t border-white/[0.06] px-3 py-2 space-y-2">
        <p className="text-[11px] text-slate-400">
          Akten-Titel: <span className="text-slate-200">{previewTitle}</span>
        </p>
        <p className="text-[11px] text-slate-500">
          Optional Dokument anhängen — oder Akte mit diesem Titel anlegen.
        </p>
        <div className="flex flex-wrap gap-1.5">
          {editing ? (
            <>
              <button
                type="button"
                disabled={disabled}
                onClick={onSave}
                className="flex items-center gap-1 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-2 py-1 text-[11px] text-emerald-100 hover:bg-emerald-500/20 disabled:opacity-50"
              >
                <Check className="h-3 w-3" />
                Speichern
              </button>
              <button
                type="button"
                disabled={disabled}
                onClick={onCancel}
                className="rounded-md border border-white/10 bg-white/[0.04] px-2 py-1 text-[11px] text-slate-300 hover:bg-white/[0.08] disabled:opacity-50"
              >
                Abbrechen
              </button>
            </>
          ) : (
            <button
              type="button"
              disabled={disabled}
              onClick={onStartEdit}
              className="flex items-center gap-1.5 rounded-md border border-white/10 bg-white/[0.04] px-2 py-1 text-[11px] text-slate-200 hover:bg-white/[0.08] disabled:opacity-50"
              aria-label="Mandatsannahme bearbeiten"
            >
              <Pencil className="h-3 w-3" />
              Bearbeiten
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
